import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SponsorsBanner from "../components/SponsorsBanner";
import { Spot, SpotLoader } from "../components/Spot";
import { ArrowRight, MapPin, Star } from "lucide-react";

const CATEGORIES = ["Tous", "Café", "Boulangerie", "Pâtisserie", "Salon de thé"];

export default function Discover() {
  const { t, lang } = useI18n();
  const [cafes, setCafes] = useState([]);
  const [sponsored, setSponsored] = useState([]);
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("Tous");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sponsored").then((r) => setSponsored(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (city.trim()) params.city = city.trim();
      if (category && category !== "Tous") params.category = category;
      if (search.trim()) params.q = search.trim();
      try {
        const { data } = await api.get("/cafes", { params });
        setCafes(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [city, category, search]);

  return (
    <div data-testid="discover-page" className="min-h-screen text-[#F5F0EB]">
      <Header />

      <section className="pt-40 pb-12 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto flex items-end justify-between gap-6">
          <div>
            <div className="overline mb-4">{t("footer.region")}</div>
            <h1 className="font-serif text-5xl md:text-7xl font-light">{t("discover.title")}</h1>
            <p className="mt-4 text-[#A8A39D]">{t("discover.subtitle")}</p>
          </div>
          <Spot variant="walk" testId="discover-header-spot" alt="" className="spot-breathe w-20 md:w-28 h-auto shrink-0" />
        </div>
      </section>

      <section className="px-6 md:px-12 py-6 border-y border-white/5 sticky top-[72px] bg-[#0A0908]/90 backdrop-blur-xl z-30">
        <div className="max-w-[1400px] mx-auto flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2 overflow-x-auto">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`cat-filter-${c}`} onClick={() => setCategory(c)} className={`px-4 py-2 text-xs tracking-[0.2em] uppercase whitespace-nowrap transition-all duration-300 ${category === c ? "bg-[#C5A059] text-[#0A0908]" : "text-[#A8A39D] hover:text-[#F5F0EB] border border-white/10"}`}>{c}</button>
            ))}
          </div>
          <div className="flex-1 grid sm:grid-cols-2 gap-3 min-w-[260px]">
            <input data-testid="search-filter" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Recherche par nom..." className="luxe-input" />
            <input data-testid="city-filter" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville (Montréal, Québec, Laval...)" className="luxe-input" />
          </div>
        </div>
      </section>

      {sponsored.length > 0 && (
        <section data-testid="sponsored-banner" className="px-6 md:px-12 py-10 bg-[#0E0C0B] border-b border-white/5">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Star size={14} className="text-[#C5A059]" />
              <div className="overline">Vedettes du Québec · Premium</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {sponsored.slice(0, 4).map((c) => (
                <Link key={c.id} to={`/cafe/${c.slug}`} data-testid={`sponsored-${c.slug}`} className="luxe-card group block relative">
                  <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-[#C5A059] text-[#0A0908] text-[9px] tracking-widest uppercase flex items-center gap-1">
                    <Star size={9} /> Premium
                  </div>
                  <div className="aspect-square overflow-hidden bg-[#141210]">
                    {c.banner_url ? <img src={c.banner_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" /> : <div className="w-full h-full flex items-center justify-center text-[#5D4037] font-serif text-5xl">{c.name?.[0]}</div>}
                  </div>
                  <div className="p-4">
                    <div className="font-serif text-lg leading-tight">{c.name}</div>
                    <div className="text-[10px] text-[#A8A39D] tracking-widest uppercase mt-1">{c.city || c.category}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="px-6 md:px-12 py-16">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <SpotLoader className="py-16" label={lang === "en" ? "Spot is looking" : "Spot cherche"} />
          ) : cafes.length === 0 ? (
            <div data-testid="empty-state" className="border border-white/5 py-20 px-6 text-center">
              <div className="relative inline-block mb-6">
                <div className="spot-halo absolute -inset-6" />
                <Spot variant="wink" testId="empty-state-spot" alt="" className="relative w-32 md:w-36 h-auto" />
              </div>
              <div className="font-serif text-3xl mb-3">{t("discover.empty")}</div>
              <div className="text-[#A8A39D] text-sm max-w-md mx-auto">{t("sections.empty")}</div>
              <Link to="/register" className="btn-gold mt-8 inline-flex"><ArrowRight size={14} /> {t("hero.cta2")}</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cafes.map((c) => (
                <Link key={c.id} to={`/cafe/${c.slug}`} data-testid={`cafe-card-${c.slug}`} className="luxe-card group block relative">
                  {c.is_draft && (
                    <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-[#0A0908]/80 text-[#A8A39D] text-[10px] tracking-widest uppercase border border-white/20">
                      Bientôt
                    </div>
                  )}
                  {c.sponsored && (
                    <div className="absolute top-3 right-3 z-10 px-2 py-1 bg-[#C5A059] text-[#0A0908] text-[10px] tracking-widest uppercase flex items-center gap-1">
                      <Star size={10} /> Sponsorisé
                    </div>
                  )}
                  <div className="aspect-[4/3] overflow-hidden bg-[#141210]">
                    {c.banner_url ? (
                      <img src={c.banner_url} alt={c.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#5D4037] font-serif text-6xl">{c.name?.[0] || "?"}</div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="overline mb-2 text-xs">{c.category || "Commerce"}{c.city ? ` · ${c.city}` : ""}</div>
                    <div className="font-serif text-2xl">{c.name}</div>
                    {c.description && <p className="text-[#A8A39D] text-sm mt-2 line-clamp-2">{c.description}</p>}
                    {(c.address || c.city) && (
                      <div className="mt-4 text-xs text-[#A8A39D] flex items-center gap-2"><MapPin size={12} className="text-[#C5A059]" /> {[c.address, c.city].filter(Boolean).join(", ")}</div>
                    )}
                    <div className="mt-4 text-xs tracking-[0.2em] uppercase text-[#C5A059] flex items-center gap-2 group-hover:gap-3 transition-all">Voir la fiche <ArrowRight size={12} /></div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
      <SponsorsBanner />
      <Footer />
    </div>
  );
}
